import { selectI18N } from '@common/store/i18n/selectors/i18n.selectors';
import { ViewStateState } from '@common/store/navigation/models/viewstate.models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { ViewStateFilter } from '@src/suite/common/data-models/navigation/viewstate-filter';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { Record } from '@src/suite/common/data-models/records/record';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { SingleFeedViewStateRecord } from '@src/suite/common/data-models/records/single-feed-viewstate-record';
import { SingleItemViewStateRecord } from '@src/suite/common/data-models/records/single-item-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { StoryViewStateRecord } from '@src/suite/common/data-models/records/story-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { ViewStateLayouts } from '@src/suite/common/enums/viewstate-layouts';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { selectDateTimeFields } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectPublicationChannelsMap } from '@src/suite/common/store/records/records.selectors';

const selectViewStateState = createFeatureSelector<ViewStateState>('viewstate');

export const selectViewStateData = createSelector(selectViewStateState, state => state);
export const selectCurrentApplication = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.applicationName;
});
export const selectCurrentViewState = createSelector(
    selectViewStateData,
    selectPublicationChannelsMap,
    (state: ViewStateState, publicationChannelsMap: Map<string, StorytellingPublicationChannelRecord>) => {
        if (state.viewState instanceof PublicationViewStateRecord && state.viewState.publicationChannelGUID) {
            (state.viewState as PublicationViewStateRecord).publicationChannel = publicationChannelsMap?.get(state.viewState.publicationChannelGUID);
        }
        return state.viewState;
    }
);
export const selectCurrentViewStateTitle = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs ? vs.title : null;
});
export const selectCurrentItem = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.slideshowViewState;
});
export const selectMainCta = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.mainCta;
});
export const selectSecondaryCta = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.secondaryCta;
});
export const selectViewOptions = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.viewOptions;
});
export const selectRightSlot = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.rightSlot;
});
export const selectNavigationViewStateFilter = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof SingleViewStateRecord) {
        return vs.viewStateFilter;
    }
    return null;
});
export const selectViewStateFilters = createSelector(selectCurrentViewState, selectNavigationViewStateFilter, (vs: ViewStateRecord, filter: ViewStateFilter) => {
    if (vs.viewStateType === ViewStateTypes.TOPIC || vs.viewStateType === ViewStateTypes.ASSIGNMENT || vs.viewStateType === ViewStateTypes.SCRIPT || vs.viewStateType === ViewStateTypes.ROLIST) {
        const vsRec = <TopicViewStateRecord>vs;

        if (vsRec && ((vsRec.viewStateLayout && vsRec.viewStateLayout.Value !== 'map') || vsRec.viewStateType === ViewStateTypes.ASSIGNMENT || vsRec.viewStateType === ViewStateTypes.SCRIPT)) {
            return filter.allFilters.filter(f => f.ID !== PSTFieldNames.PSTTeamLocation);
        }
        return filter.allFilters;
    }
    return filter.allFilters.filter(f => f.ID !== PSTFieldNames.PSTTeamLocation);
});
export const selectNavigationViewStateAllFilters = createSelector(selectNavigationViewStateFilter, (filter: ViewStateFilter) => {
    return filter ? filter.allFilters : null;
});
export const selectNavigationViewStateRequiredFilters = createSelector(selectNavigationViewStateFilter, (filter: ViewStateFilter) => {
    return filter ? filter.requiredFilters : null;
});
export const selectNavigationViewStateRequiredFiltersIDs = createSelector(selectNavigationViewStateRequiredFilters, (filters: Array<FilterDef>) => {
    return filters ? filters.map(item => item.ID) : null;
});
export const selectNavigationViewStateOptionalFilters = createSelector(selectNavigationViewStateFilter, (filter: ViewStateFilter) => {
    return filter ? filter.optionalFilters : null;
});
export const selectNavigationViewStateOptionalFiltersIDs = createSelector(selectNavigationViewStateOptionalFilters, (filters: Array<FilterDef>) => {
    return filters ? filters.map(item => item.ID) : null;
});
export const selectViewStateFilterState = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs.viewStateFilterState;
});
export const selectCurrentViewStateType = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs?.viewStateType;
});
export const selectCurrentLayout = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => (vs ? (vs as SingleViewStateRecord)?.viewStateLayout?.Value : null));

export const selectCurrentGroupBy = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs ? (vs as SingleViewStateRecord)?.viewStateGrouping?.Value : null;
});

export const selectCurrentGroupByOrder = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => (vs ? (vs as SingleViewStateRecord)?.viewStateGroupSortOrder : null));
export const selectCurrentGroupByOrderIcon = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (!vs) return null;
    const icon = (vs as SingleViewStateRecord)?.viewStateGroupSortOrder === ViewStateSortOders.ASC ? 'arrow_upward' : 'arrow_downward';
    return icon;
});
export const selectCurrentGroupByOrderTitle = createSelector(selectCurrentGroupByOrder, selectI18N, (sortBy: string, i18n) => {
    if (!sortBy) return null;
    return sortBy === ViewStateSortOders.ASC ? i18n?.PSTWebSortOrderTooltipASC : i18n?.PSTWebSortOrderTooltipDESC;
});
export const selectCurrentSortBy = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs ? (vs as SingleViewStateRecord)?.viewStateSorting?.Value : null;
});
export const selectIsViewStateSortingManual = createSelector(selectCurrentSortBy, (sortBy: string) => (sortBy ? sortBy === PSTFieldNames.PSTTopicOrder : false));
export const selectCurrentSortByOrder = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => (vs ? (vs as SingleViewStateRecord)?.viewStateSortOrder : null));
export const selectCurrentSortByOrderIcon = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (!vs) return null;
    const icon = (vs as SingleViewStateRecord)?.viewStateSortOrder === ViewStateSortOders.ASC ? 'arrow_upward' : 'arrow_downward';
    return icon;
});
export const selectCurrentSortByOrderTitle = createSelector(selectCurrentSortByOrder, selectI18N, (sortBy: string, i18n) => {
    if (!sortBy) return null;
    return sortBy === ViewStateSortOders.ASC ? i18n?.PSTWebSortOrderTooltipASC : i18n?.PSTWebSortOrderTooltipDESC;
});
export const selectViewHasFilter = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return !(vs instanceof SingleItemViewStateRecord || vs instanceof PublicationViewStateRecord);
});
export const selectIsSingleViewStateRecord = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs instanceof SingleViewStateRecord;
});
export const selectSaveAsViewSettingsEnabled = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    return vs instanceof SearchViewStateRecord;
});
export const selectSaveViewSettingsEnabled = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs?.path?.isTeamView && !Record.nomWebServicesJson?.HelloResults?.Supervisor) return false;
    return true;
});
export const selectViewStatePublicationRevisionNumber = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.publicationRevisionNumber;
    }
    return null;
});
export const selectViewStateMaxPublicationRevisionNumber = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) return vs.maxPublicationRevisionNumber;
    return null;
});
// export const selectViewStatePublicationChannel = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
//     if (vs instanceof PublicationViewStateRecord) return vs.publicationChannel;
//     return null;
// });
export const selectViewStateCustomCardItems = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (
        vs instanceof TopicViewStateRecord ||
        vs instanceof StoryViewStateRecord ||
        vs instanceof SingleFeedViewStateRecord ||
        vs instanceof SingleViewStateRecord ||
        vs instanceof PublicationViewStateRecord
    )
        return vs.viewStateCustomCardItems;
    return [];
});
export const selectViewStateSearchString = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof SearchViewStateRecord) return vs.viewStateSearchQuery;
    return '';
});
export const selectViewStatePublicationRevisionsList = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.publicationRevisionsList;
    }
    return null;
});
export const selectViewStateMapZoomToCoverage = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof TopicViewStateRecord) return vs.viewStateMapZoomToCoverage;
    return false;
});
export const selectViewStateMapShowTeamLocations = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof TopicViewStateRecord) return vs.viewStateMapShowTeamLocations;
    return false;
});
export const selectViewStateMapLockMapLocation = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof TopicViewStateRecord) return vs.viewStateMapLockMapLocation;
    return false;
});
export const selectViewStateCurrentZoom = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof TopicViewStateRecord) return vs.viewStateMapCurrentZoom;
    return null;
});
export const selectViewStateCurrentMapCenter = createSelector(selectCurrentViewState, (vs: ViewStateRecord) => {
    if (vs instanceof TopicViewStateRecord) return vs.viewStateCurrentMapCenter;
    return null;
});
//

export const selectViewStateFilterLabel = createSelector(selectCurrentViewState, selectI18N, (vs: ViewStateRecord, i18n) => {
    if (vs) {
        if (vs instanceof SearchViewStateRecord) {
            return i18n?.PSTWebRefineSearchLabel;
        } else if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
            return i18n?.PSTWebFilterLabel;
        }
    }

    return 'Filter';
});
export const selectFilterElevation = createSelector(selectViewStateData, (state: ViewStateState) => {
    return state.filterElevation;
});

export const selectIsCardLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.CARD;
});
export const selectIsCondenseLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.CONDENSE;
});
export const selectIsCastCardLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.CAST_CARD;
});
export const selectIsTileLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.TILE;
});
export const selectIsMapLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.MAP;
});
export const selectIsTrendsLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.TRENDS;
});
export const selectIsWireLayout = createSelector(selectCurrentLayout, (layout: string) => {
    return layout === ViewStateLayouts.WIRE;
});
export const selectGroupByDateTimeField = createSelector(selectDateTimeFields, selectCurrentGroupBy, (dateTimeFields, groupBy) => {
    return dateTimeFields[groupBy];
});
